import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import { ENDPOINTS } from "../../services/endpoints";
import { IMAGES } from "../../constants/image";
import resetConfig from "../../config/forms/reset.json";
import { validateValue } from "../../utils/validation";
import usePopup from "../../hooks/usePopup";
import { extractMessageResponse } from "../../utils/messageResponse";

interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  matchField?: string;
}

interface FormField {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  validation?: FieldValidation;
}

const getApiMessage = (value: any, fallback: string): string => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter(Boolean).join(" ") || fallback;
  if (typeof value === "object") {
    const message = value.error || value.message || value.errors;
    if (message) return getApiMessage(message, fallback);
    return (
      Object.values(value)
        .map((item) => getApiMessage(item, ""))
        .filter(Boolean)
        .join(" ") || fallback
    );
  }
  return String(value);
};

const PASSWORD_RULES = [
  {
    label: "At least 8 characters",
    test: (value: string) => value.length >= 8,
  },
  {
    label: "At least 1 uppercase letter",
    test: (value: string) => /[A-Z]/.test(value),
  },
  {
    label: "At least 1 lowercase letter",
    test: (value: string) => /[a-z]/.test(value),
  },
  {
    label: "At least 1 number",
    test: (value: string) => /\d/.test(value),
  },
  {
    label: "At least 1 special character",
    test: (value: string) => /[^A-Za-z0-9\s]/.test(value),
  },
  {
    label: "No spaces",
    test: (value: string) => !/\s/.test(value),
  },
];

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const showPopup = usePopup();
  // const [formData, setFormData] = useState<any>(() => ({
  //   email: searchParams.get("email") || "",
  //   token: searchParams.get("token") || "",
  // }));
  const token = searchParams.get("token") || "";
const [formData, setFormData] = useState<any>({
  email: "",
  token,
  new_password: "",
  confirm_password: "",
});
useEffect(() => {
  const fetchEmail = async () => {
    if (!token) {
      setErrors({
        token: "Reset token is missing.",
      });
      return;
    }

    try {
      const response = await api.get(
        `${ENDPOINTS.AUTH.VALIDATE_RESET_LINK}?token=${encodeURIComponent(token)}`,
      );

      setFormData((prev: any) => ({
        ...prev,
        email: response.data.email || "",
      }));
    } catch (error) {
      setErrors({
        token: "Invalid or expired reset link.",
      });
    }
  };

  fetchEmail();
}, [token]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [activeInfoField, setActiveInfoField] = useState<string | null>(null);

  const fields = resetConfig.fields as FormField[];
  const ICON_MAP: Record<string, string> = {
    email: IMAGES.ICONS.EMAIL,
    new_password: IMAGES.CHANGEPASSWORD.PASSWORD_ICON,
    confirm_password: IMAGES.CHANGEPASSWORD.PASSWORD_ICON,
  };
  const toggleIconMap: Record<string, string> = {
    new_password: IMAGES.CHANGEPASSWORD.EYE_ICON,
    confirm_password: IMAGES.CHANGEPASSWORD.EYE_ICON,
  };

  const validateField = (name: string, value: string, data = formData) => {
    const field = fields.find((f) => f.name === name);
    if (!field) return "";

    return (
      validateValue(
        value,
        {
          label: field.label,
          ...(field.validation || {}),
        },
        data,
      ) || ""
    );
  };
  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    fields.forEach((field) => {
      const error = validateField(field.name, formData[field.name] || "");
      if (error) nextErrors[field.name] = error;
    });

    if (!formData.token) {
      nextErrors.token =
        "Reset token is missing. Please use the link sent to your email.";
    }

    return nextErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextFormData = { ...formData, [name]: value };
    setFormData(nextFormData);

    setErrors((prev) => ({
      ...prev,
      [name]:
        touched[name] || prev[name]
          ? validateField(name, value, nextFormData)
          : "",
      ...(name === "new_password" &&
      nextFormData.confirm_password &&
      (touched.confirm_password || prev.confirm_password)
        ? {
            confirm_password:
              validateField(
                "confirm_password",
                nextFormData.confirm_password,
                nextFormData,
              ) || "",
          }
        : {}),
    }));
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, formData[name] || ""),
    }));
  };

  const togglePassword = (fieldName: string) => {
    setShowPassword((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const renderPasswordInfo = (fieldName: string) => {
    if (fieldName !== "new_password" && fieldName !== "confirm_password") {
      return null;
    }

    const passwordValue = String(formData.new_password || "");
    const confirmValue = String(formData.confirm_password || "");
    const rules =
      fieldName === "confirm_password"
        ? [
            {
              label: "Matches the new password",
              valid: Boolean(confirmValue) && confirmValue === passwordValue,
            },
          ]
        : PASSWORD_RULES.map((rule) => ({
            label: rule.label,
            valid: rule.test(passwordValue),
          }));

    return (
      <span className={`password-info ${activeInfoField === fieldName ? "is-open" : ""}`}>
        <button
          type="button"
          className="password-info-trigger"
          aria-label={`${fieldName === "confirm_password" ? "Confirm password" : "Password"} rules`}
          aria-expanded={activeInfoField === fieldName}
          onClick={() =>
            setActiveInfoField((current) =>
              current === fieldName ? null : fieldName,
            )
          }
          onBlur={() => {
            window.setTimeout(() => setActiveInfoField(null), 120);
          }}
        >
          i
        </button>
        <span className="password-info-popover" role="tooltip">
          {rules.map((rule) => (
            <span
              className={`password-rule ${
                rule.valid ? "is-valid" : "is-invalid"
              }`}
              key={rule.label}
            >
              <span className="password-rule-icon">
                {rule.valid ? "✓" : "×"}
              </span>
              {rule.label}
            </span>
          ))}
        </span>
      </span>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setTouched(
        fields.reduce<Record<string, boolean>>((next, field) => {
          next[field.name] = true;
          return next;
        }, {}),
      );
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
   const response = await api.post(ENDPOINTS.AUTH.RESET_PASSWORD, {
     token: formData.token,
     password: formData.new_password,
     password_confirmation: formData.confirm_password,
   });

      const messageResponse = extractMessageResponse(
        response?.data,
        "Your password has been reset successfully.",
        200,
      );
      showPopup({
        status: messageResponse.status,
        message: messageResponse.message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
        onOk: () => navigate("/login"),
      });
    } catch (error: any) {
      const apiData = error?.response?.data;
      const messageResponse = extractMessageResponse(apiData, "Reset password failed", 500);
      showPopup({
        status: messageResponse.status,
        message: messageResponse.message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-wrapper">
      <div className="reset-background">
        <div className="reset-card-container">
          <div className="card reset-card shadow">
            <div className="card-body">
              <h3 className="text-center fw-bold mb-1 text-white">
                Reset Password
              </h3>
              <p className="text-center text-white mb-1">
                *All Fields are Mandatory
              </p>
              {errors.token && (
                <div className="invalid-feedback d-block">{errors.token}</div>
              )}
              <form onSubmit={handleSubmit}>
                {fields.map((field) => {
                  const value = formData[field.name] || "";
                  const error = errors[field.name];

                  return (
                    <div className="mb-2 position-relative" key={field.name}>
                      <label className="form-label password-field-label">
                        <span>{field.label}</span>
                        {renderPasswordInfo(field.name)}
                      </label>

                      <div className="reset-input-wrapper">
                        <input
                          type={
                            field.type === "password"
                              ? showPassword[field.name]
                                ? "text"
                                : "password"
                              : field.type
                          }
                          name={field.name}
                          className={`form-control custom-input ${
                            error ? "is-invalid" : ""
                          }`}
                          placeholder={field.placeholder}
                          value={value}
                          onChange={handleChange}
                          onBlur={() => handleBlur(field.name)}
                          readOnly={field.name === "email"}
                        />

                        {ICON_MAP[field.name] && (
                          <img
                            src={ICON_MAP[field.name]}
                            alt={field.name}
                            className="input-icon"
                          />
                        )}

                        {field.type === "password" && (
                          <img
                            src={
                              showPassword[field.name]
                                ?  toggleIconMap[field.name]
                                : IMAGES.CHANGEPASSWORD.EYE_HIDE_ICON
                            }
                           
                            alt={
                              showPassword[field.name]
                                ? "show password"
                                : "Hide password"
                            }
                            className="password-toggle"
                            onClick={() => togglePassword(field.name)}
                          />
                        )}
                      </div>

                      {error && (
                        <div className="invalid-feedback d-block">{error}</div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Processing..." : "Reset Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ResetPassword;
